import type { ReactNode } from "react"
import { useCallback, useRef, useState } from "react"

import { FileText, Paperclip, Rocket, Sparkles, X } from "@/components/icons"
import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import { acceptedFileTypes } from "@/app/pages/modules/ai-explorer-constants"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

type DashAiPromptProps = {
  onSend: (question: string) => void
  disableSend?: boolean
  files?: File[]
  onAddFiles?: (files: File[]) => void
  onRemoveFile?: (file: File) => void
  modelControl?: ReactNode
}

export function DashAiPrompt({
  onSend,
  disableSend = false,
  files = [],
  onAddFiles,
  onRemoveFile,
  modelControl,
}: DashAiPromptProps) {
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = () => {
    const trimmed = prompt.trim()
    if (!trimmed || disableSend) return
    onSend(trimmed)
    setPrompt("")
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleBoxClick = () => {
    textareaRef.current?.focus()
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onAddFiles?.(acceptedFiles)
    },
    [onAddFiles]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const hasFiles = files.length > 0

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div
        role="presentation"
        {...getRootProps()}
        className={cn(
          "bg-card cursor-text rounded-lg border shadow-sm",
          "transition-[box-shadow,border-color] duration-300",
          "focus-within:shadow-primary/10 focus-within:ring-primary/30 focus-within:shadow-lg focus-within:ring-1",
          isDragActive && "border-primary ring-primary/30 ring-1"
        )}
        onClick={handleBoxClick}
      >
        <input {...getInputProps()} />

        {isDragActive && (
          <div className="text-primary flex items-center justify-center gap-2 px-4 pt-3 text-sm font-medium">
            {t("dash.landing.aiPrompt.dropFiles")}
          </div>
        )}

        <Textarea
          ref={textareaRef}
          placeholder={t("dash.landing.aiPrompt.placeholder")}
          rows={3}
          value={prompt}
          className={cn(
            "max-h-[200px] min-h-[80px] resize-none rounded-t-lg border-0 bg-transparent px-4 pt-4 pb-2",
            "placeholder:text-muted-foreground/50 text-base shadow-none focus-visible:ring-0"
          )}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        {hasFiles && (
          <div className="flex flex-wrap gap-2 px-3 pb-2">
            {files.map((file) => (
              <div
                key={`${file.name}-${file.size}`}
                className="bg-muted/50 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs"
              >
                <FileText className="text-muted-foreground size-3.5 shrink-0" />
                <span className="max-w-[150px] truncate">{file.name}</span>
                <span className="text-muted-foreground">({(file.size / 1024).toFixed(0)}KB)</span>
                {onRemoveFile && (
                  <button
                    className="text-muted-foreground hover:text-foreground -m-0.5 ml-0.5 flex size-6 items-center justify-center rounded-full transition-colors"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemoveFile(file)
                    }}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between px-3 pb-3">
          <div className="flex items-center gap-2">
            {modelControl}
            {onAddFiles && (
              <Button
                className="size-8 rounded-lg p-0"
                size="sm"
                type="button"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  open()
                }}
              >
                <Paperclip className="size-4" />
              </Button>
            )}
            <div className="text-muted-foreground flex items-center gap-1.5">
              <Sparkles className="size-3.5" />
              <span className="text-xs">{t("dash.landing.aiPrompt.aiPowered")}</span>
            </div>
          </div>
          <Button
            className="h-8 rounded-lg"
            disabled={disableSend || !prompt.trim()}
            size="sm"
            onClick={handleSend}
          >
            <Rocket className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
